import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { LineChart, Line, AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { V, Haptic } from '../../utils/theme';
import { LS } from '../../utils/storage';
import { Icons } from '../../components/Icons';
import { Card, Btn, Field, Sheet, Chip, Stat, Progress, Skeleton, SkeletonCard, SuccessToastCtrl, ConfirmCtrl, MsgBannerCtrl } from '../../components/ui';
import { today, ago, fmtShort, fmtFull, uid, calc1RM, calcPlates, PLATES, convW, wUnit, dUnit, isCardio, toKg, toLbs, friendDisplayName, chartCfg, liftsIn } from '../../utils/helpers';
import { CloudSync, SocialAPI, SyncQueue, SYNC_URL, APP_VERSION } from '../../utils/sync';
import { apiPost } from '../../utils/api';
import { Undo } from '../../utils/undo';
import { useLayout } from '../../utils/responsive';
import { ShareCard } from '../../utils/share';
import { appName } from '../../utils/brand';
import { flagOn } from '../../utils/org';
import { BADGE_DEFS, calcEarnedBadges } from '../../data/badges';
import { TEMPLATES } from '../../data/templates';
import { IRON_RANKS } from '../../data/ranks';
import { FF_HACKS, FF_BRANDS } from '../../data/hacks';
import { FOODS } from '../../data/foods';
import { AuthToken } from '../../utils/auth';
import { calcReadiness, useStreak, usePRs, useNutritionStreak } from '../../components/dialogs';
import { onAccent } from '../../components/kit';
import {
  normalizeInjuriesFromWire, injuryIsActive, injuryIsResolved, injuryJoint,
  normalizePhasesFromWire, phaseIsActive, phaseTypeId, phaseStart, phaseEnd,
} from '../../utils/healthWire';

export function GoalEngine({s,d}){
  const { isDesktop } = useLayout();
  const [showAdd,setShowAdd]=useState(false);
  const [form,setForm]=useState({type:"weight",target:"",deadline:"",label:""});

  // B3 fix: s.milestones mixes structured goals (type/target/deadline) with freeform
  // log entries (text/date from demo or auto-milestones). Only render structured goals here.
  // Structured goals (type/target/deadline) — safe to modify
  const goals=(s.milestones||[]).filter(g=>g.target!=null&&g.deadline);
  // Freeform entries (auto-milestones, log entries) — must be preserved on every write
  const freeformMilestones=(s.milestones||[]).filter(g=>g.target==null||!g.deadline);

  const addGoal=()=>{
    if(!form.target||!form.deadline)return;
    const g={id:uid(),type:form.type,target:parseFloat(form.target),deadline:form.deadline,
      label:form.label||`${form.type==="weight"?"Reach":"Hit"} ${form.target}`,
      created:today(),completed:false};
    d({type:"SET_MILESTONES",milestones:[...freeformMilestones,...goals,g]});
    SuccessToastCtrl.show("Goal created");
    setForm({type:"weight",target:"",deadline:"",label:""});
    setShowAdd(false);
  };

  const deleteGoal=(id)=>{d({type:"SET_MILESTONES",milestones:[...freeformMilestones,...goals.filter(g=>g.id!==id)]});SuccessToastCtrl.show("Goal deleted");};
  const completeGoal=(id)=>{d({type:"SET_MILESTONES",milestones:[...freeformMilestones,...goals.map(g=>g.id===id?{...g,completed:true,completedDate:today()}:g)]});SuccessToastCtrl.show("Goal completed! 🎉");};

  const calcProgress=(g)=>{
    if(g.type==="weight"){
      // B6 fix: sort desc to get most recent entry with a weight reading
      const sortedBody=[...s.body].sort((a,b)=>b.date.localeCompare(a.date));
      const bw=sortedBody.find(b=>b.weight)?.weight||0;
      // Baseline = weight at/just before the goal was created (not the all-time-oldest
      // entry) so progress is measured from when the user set the goal.
      const startEntry=g.created
        ? (sortedBody.find(b=>b.weight&&b.date<=g.created)||sortedBody[sortedBody.length-1])
        : sortedBody[sortedBody.length-1];
      const startBW=startEntry?.weight||bw;
      if(!startBW||!bw)return 0;
      // Only movement TOWARD the target counts (gaining on a cut used to read as progress)
      const dir=Math.sign(g.target-startBW)||1;
      const total=Math.abs(g.target-startBW);
      const done=Math.max(0,(bw-startBW)*dir);
      return total>0?Math.min(100,Math.round((done/total)*100)):0;
    }
    if(g.type==="strength"){
      // B7 fix: find max weight only for matching lift, not all exercises
      const labelLower=(g.label||"").toLowerCase();
      const liftKeys=["bench","squat","deadlift","ohp","press","row","curl","pull"];
      const matchKey=liftKeys.find(k=>labelLower.includes(k));
      let maxW=0;
      s.workouts.forEach(w=>w.exercises.forEach(ex=>{
        const exId=(ex.exerciseId||"").toLowerCase();
        const exName=(s.exercises?.find(e=>e.id===ex.exerciseId)?.name||"").toLowerCase();
        // If label mentions a specific lift, only count that lift; otherwise any lift
        const matches=!matchKey||(exId.includes(matchKey)||exName.includes(matchKey));
        if(matches)ex.sets.forEach(st=>{const w2=parseFloat(st.weight)||0;if(w2>maxW)maxW=w2;});
      }));
      // Lifts entered during onboarding seed the baseline until logged sets beat them
      const seed=liftsIn(s.profile?.lifts,s.units);
      if(seed&&matchKey&&seed[matchKey])maxW=Math.max(maxW,seed[matchKey]);
      return maxW>=g.target?100:Math.min(99,Math.round((maxW/g.target)*100));
    }
    if(g.type==="nutrition"){
      const last30=s.nutrition.filter(n=>n.date>=ago(30));
      const hits=last30.filter(n=>n.protein>=s.goals.protein*.8).length;
      const target=parseInt(g.target)||25;
      return Math.min(100,Math.round((hits/target)*100));
    }
    if(g.type==="consistency"){
      const last30=s.workouts.filter(w=>w.date>=ago(30)).length;
      return Math.min(100,Math.round((last30/parseInt(g.target||16))*100));
    }
    return 0;
  };

  const daysLeft=(deadline)=>{
    if(!deadline)return null;
    const d2=Math.ceil((new Date(deadline)-new Date(today()))/(86400000));
    return d2;
  };

  const types=[
    {id:"weight",label:"Weight Goal",icon:"⚖️",ex:"Lose 10 lbs by summer"},
    {id:"strength",label:"Strength PR",icon:"💪",ex:"Bench 225 lbs"},
    {id:"nutrition",label:"Nutrition Streak",icon:"🥩",ex:"Hit protein 25/30 days"},
    {id:"consistency",label:"Consistency",icon:"🔥",ex:"Train 16x this month"},
  ];

  return(
    <div style={{display:"flex",flexDirection:"column",gap:12}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <div style={{fontSize:16,fontWeight:800,color:V.text}}>Goals & Milestones</div>
        <button onClick={()=>setShowAdd(true)} style={{padding:"6px 12px",borderRadius:8,
          background:`${V.accent}12`,border:`1px solid ${V.accent}25`,cursor:"pointer",
          WebkitTapHighlightColor:"transparent",fontSize:11,fontWeight:700,color:V.accent,fontFamily:V.font}}>
          + New Goal
        </button>
      </div>

      {goals.filter(g=>!g.completed).length===0&&!showAdd&&(
        <Card style={{padding:20,textAlign:"center"}}>
          <div style={{fontSize:28,marginBottom:8}}>🎯</div>
          <div style={{fontSize:13,fontWeight:600,color:V.text,marginBottom:4}}>No active goals</div>
          <div style={{fontSize:11,color:V.text3}}>Set a target to stay focused and track progress</div>
        </Card>
      )}

      {goals.filter(g=>!g.completed).map(g=>{
        const pct=calcProgress(g);
        const dl=daysLeft(g.deadline);
        const c=pct>=100?V.accent:dl===null?V.accent2:dl<0?V.danger:dl<7?V.warn:V.accent2;
        return(
          <Card key={g.id} style={{padding:14}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
              <div>
                <div style={{fontSize:13,fontWeight:700,color:V.text}}>{g.label}</div>
                <div style={{fontSize:11,color:V.text3,marginTop:2}}>
                  {dl===null?null:dl>=0?`${dl} days left`:<span style={{color:V.danger}}>Overdue</span>}
                  {" · "}Target: {g.target}{g.type==="weight"?` ${s.units}`:g.type==="nutrition"?" days":""}
                </div>
              </div>
              <div style={{display:"flex",gap:6}}>
                {pct>=100&&<button onClick={()=>completeGoal(g.id)} style={{padding:"4px 8px",borderRadius:6,
                  background:`${V.accent}15`,border:"none",cursor:"pointer",fontSize:11,color:V.accent,fontWeight:700}}>✓ Done</button>}
                <button onClick={()=>deleteGoal(g.id)} style={{padding:"4px 8px",borderRadius:6,
                  background:`${V.danger}14`,border:"none",cursor:"pointer",fontSize:11,color:V.danger}}>✕</button>
              </div>
            </div>
            <Progress val={pct} max={100} color={c} h={8}/>
            <div style={{fontSize:11,color:c,fontWeight:700,marginTop:4,fontFamily:V.mono}}>{pct}%</div>
          </Card>
        );
      })}

      {/* Completed goals */}
      {goals.filter(g=>g.completed).length>0&&(
        <div>
          <div style={{fontSize:12,fontWeight:600,color:V.text3,marginBottom:6}}>Completed</div>
          {goals.filter(g=>g.completed).map(g=>(
            <Card key={g.id} style={{padding:10,marginBottom:6,opacity:0.6}}>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <span style={{color:V.accent}}>✓</span>
                <span style={{fontSize:12,color:V.text,fontWeight:600}}>{g.label}</span>
                <span style={{flex:1}}/>
                <span style={{fontSize:11,color:V.text3}}>{g.completedDate}</span>
                <button onClick={()=>deleteGoal(g.id)} style={{background:"none",border:"none",cursor:"pointer",
                  fontSize:11,color:V.text3,padding:"2px 6px"}}>✕</button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {showAdd&&(
        <Sheet title="New Goal" onClose={()=>setShowAdd(false)}>
          <div style={{display:"flex",flexWrap:"wrap",gap:8,marginBottom:12}}>
            {types.map(t=>(
              <button key={t.id} onClick={()=>setForm(f=>({...f,type:t.id}))} style={{
                padding:"10px 14px",borderRadius:10,border:`1.5px solid ${form.type===t.id?V.accent:V.cardBorder}`,
                background:form.type===t.id?`${V.accent}10`:V.card,
                cursor:"pointer",WebkitTapHighlightColor:"transparent",textAlign:"left"
              }}>
                <div style={{fontSize:14}}>{t.icon}</div>
                <div style={{fontSize:11,fontWeight:600,color:form.type===t.id?V.accent:V.text,marginTop:2}}>{t.label}</div>
                <div style={{fontSize:11,color:V.text3}}>{t.ex}</div>
              </button>
            ))}
          </div>
          <Field label="Label" value={form.label} onChange={v=>setForm(f=>({...f,label:v}))} placeholder="e.g. Bench 225 by August"/>
          <Field label="Target Number" type="number" value={form.target} onChange={v=>setForm(f=>({...f,target:v}))}
            placeholder={form.type==="weight"?"Target weight":form.type==="strength"?"Weight to hit":form.type==="nutrition"?"Days to hit":"Sessions per month"}/>
          <Field label="Deadline" type="date" value={form.deadline} onChange={v=>setForm(f=>({...f,deadline:v}))}/>
          <Btn full onClick={addGoal} s={{marginTop:8}}>{Icons.check({size:16,color:onAccent()})} Set Goal</Btn>
        </Sheet>
      )}
    </div>
  );
}

// ─── #1 Adaptive Program Builder ───
export function AdaptiveCoach({s,d}){
  const { isDesktop } = useLayout();
  const r=useMemo(()=>calcReadiness(s),[s.workouts,s.nutrition,s.checkins,s.goals]);
  const dow=new Date().getDay();
  const todayType=s.schedule?.overrides?.[today()]||s.schedule?.weekly?.[dow]||"Rest";
  const u=wUnit(s.units);

  // Generate today's recommended workout based on schedule + readiness
  const program=useMemo(()=>{
    if(todayType==="Rest")return{type:"rest",message:"Recovery day. Focus on stretching, mobility, or light cardio.",exercises:[]};

    // Map muscle groups to exercises
    const muscleMap={
      "Push":["bench","incbench","ohp","lateralraise","tricep","dip"],
      "Pull":["deadlift","row","pullup","latpull","curl","facepull"],
      "Legs":["squat","legpress","rdl","hamcurl","hipthrust","calfraise"],
      "Chest":["bench","incbench","dbbench","flye","pecfly","dip"],
      "Back":["deadlift","row","pullup","latpull","cablerow","dbrow"],
      "Shoulders":["ohp","dbohp","lateralraise","facepull","reardelt","shrug"],
      "Arms":["curl","hammercurl","preacher","tricep","skullcrusher","ohtriext"],
      "Upper Body":["bench","row","ohp","latpull","curl","tricep"],
      "Lower Body":["squat","rdl","legpress","hamcurl","hipthrust","calfraise"],
      "Full Body":["squat","bench","row","ohp","deadlift","curl"],
      "Cardio":[]
    };

    const baseIds=muscleMap[todayType]||muscleMap["Full Body"];
    if(baseIds.length===0)return{type:"cardio",message:"Cardio day. 20-40 min steady state or HIIT.",exercises:[]};

    // Skip lifts that load an actively flagged joint, and swap in a preset substitute when one exists
    const AVOID={shoulder:["bench","incbench","dbbench","ohp","dbohp","lateralraise","dip","flye","pecfly","pullup"],
      knee:["squat","legpress","lunge","legext","frontsquat","hipthrust"],
      lower_back:["deadlift","squat","row","rdl","goodmorning","hipthrust","dbrow"],
      elbow:["curl","hammercurl","preacher","tricep","skullcrusher","ohtriext","dip"],
      wrist:["bench","ohp","curl","frontsquat","pushup"],hip:["squat","hipthrust","rdl","lunge","legpress"]};
    const avoid=new Set(normalizeInjuriesFromWire(s.injuries).filter(injuryIsActive).flatMap(i=>{
      const joint=String(injuryJoint(i)||"").toLowerCase().replace(/\s+/g,"_");
      const id=["shoulder","knee","lower_back","elbow","wrist","hip"].find(j=>joint===j||joint.includes(j)||joint.includes(j.replace("_"," ")));
      return AVOID[id]||AVOID[joint]||[];
    }));
    const skippedIds=baseIds.filter(id=>avoid.has(id));
    const exIds=baseIds.filter(id=>!avoid.has(id));
    skippedIds.forEach(id=>{const alt=(EXERCISE_SUBS[id]||[]).find(a=>!avoid.has(a)&&!exIds.includes(a)&&s.exercises.some(e=>e.id===a));if(alt)exIds.push(alt);});
    // Session length from onboarding caps the list; readiness trims it further
    const byLength={30:3,45:4,60:6,90:8}[s.profile?.sessionLength]||6;
    const count=Math.min(byLength,r.score>=60?6:r.score>=40?5:4);
    const step=s.units==="kg"?2.5:5; // kg lifters progress in 2.5s, not 5s

    // Look up last performance for each exercise
    const recs=exIds.slice(0,count).map(eid=>{
      const ex=s.exercises.find(e=>e.id===eid);
      if(!ex)return null;

      // Find last time this exercise was done
      let lastSets=null;
      for(const w of s.workouts){
        const found=w.exercises.find(e=>e.exerciseId===eid);
        if(found){lastSets=found.sets;break;}
      }

      // Volume adjustment based on readiness
      let setCount=lastSets?lastSets.length:3;
      let weightMult=1.0;
      if(r.score>=80){weightMult=1.025;} // Push for progression
      else if(r.score>=60){weightMult=1.0;} // Maintain
      else if(r.score>=40){weightMult=0.9;setCount=Math.max(2,setCount-1);} // Reduce
      else{weightMult=0.8;setCount=Math.max(2,setCount-1);} // Deload

      const targetWeight=(lastSets&&lastSets.length)?Math.round(Math.max(...lastSets.map(st=>parseFloat(st.weight)||0))*weightMult/step)*step:0;
      const targetReps=lastSets?Math.round(lastSets.reduce((s2,st)=>s2+st.reps,0)/lastSets.length):8;

      return{id:eid,name:ex.name,sets:setCount,reps:targetReps,weight:targetWeight,
        note:weightMult>1?"↑ Push weight":weightMult<0.95?"↓ Deload":"→ Maintain"};
    }).filter(Boolean);

    const skipped=skippedIds.map(id=>s.exercises.find(e=>e.id===id)?.name).filter(Boolean);
    return{type:"workout",message:r.rec,exercises:recs,skipped};
  },[s.workouts,s.exercises,s.schedule,s.injuries,s.profile?.sessionLength,s.units,todayType,r.score,r.rec]);

  if(program.type==="rest"){
    const RECOVERY=[
      {name:"Hip Flexor Stretch",sets:"2×45s each side",note:"Lunge, rear knee down, lean forward",icon:"🦵"},
      {name:"Thoracic Rotation",sets:"2×10 each side",note:"Rotate upper back slowly",icon:"🔄"},
      {name:"Foam Roll — Quads & IT Band",sets:"2min each leg",note:"Slow passes, pause on tight spots",icon:"🧘"},
      {name:"Cat-Cow Stretch",sets:"2×10 reps",note:"All fours, full spinal flexion and extension",icon:"🐈"},
      {name:"90/90 Hip Stretch",sets:"2×60s each side",note:"Front and rear hip flexors simultaneously",icon:"💺"},
      {name:"Dead Hang",sets:"3×30s",note:"Full spinal decompression — shoulders packed",icon:"🏋️"},
    ];
    return(
      <div style={{display:"flex",flexDirection:"column",gap:12}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div style={{fontSize:16,fontWeight:800,color:V.text}}>Today's Program</div>
          <span style={{fontSize:11,fontWeight:700,padding:"3px 8px",borderRadius:6,background:`${V.purple}18`,color:V.purple}}>Recovery</span>
        </div>
        <Card style={{padding:14,background:"rgba(139,92,246,0.04)",border:"1px solid rgba(139,92,246,0.15)"}}>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}>
            <span style={{fontSize:24}}>🧘</span>
            <div>
              <div style={{fontSize:14,fontWeight:700,color:V.text}}>Recovery Day</div>
              <div style={{fontSize:11,color:V.text3}}>Active recovery keeps you loose and speeds adaptation</div>
            </div>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:6}}>
            {[{l:"Mobility",v:"~20 min",c:V.purple},{l:"Intensity",v:"Low",c:V.accent2},{l:"Workout XP",v:"+0",c:V.text3}].map(st=>(
              <div key={st.l} style={{textAlign:"center",padding:"6px 4px",borderRadius:6,background:V.card}}>
                <div style={{fontSize:13,fontWeight:800,color:st.c,fontFamily:V.mono}}>{st.v}</div>
                <div style={{fontSize:11,color:V.text3}}>{st.l}</div>
              </div>
            ))}
          </div>
        </Card>
        <div style={{fontSize:11,fontWeight:700,color:V.text3,textTransform:"uppercase",letterSpacing:".08em",paddingLeft:2}}>Recovery Circuit</div>
        {RECOVERY.map((ex,i)=>(
          <Card key={i} style={{padding:12}}>
            <div style={{display:"flex",alignItems:"center",gap:10}}>
              <div style={{width:34,height:34,borderRadius:10,background:`${V.purple}18`,
                display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,flexShrink:0}}>{ex.icon}</div>
              <div style={{flex:1}}>
                <div style={{fontSize:13,fontWeight:700,color:V.text}}>{ex.name}</div>
                <div style={{fontSize:11,color:V.purple,fontWeight:600}}>{ex.sets}</div>
                <div style={{fontSize:11,color:V.text3,marginTop:2}}>{ex.note}</div>
              </div>
            </div>
          </Card>
        ))}
        <div style={{padding:"10px 14px",borderRadius:10,background:`${V.accent}0a`,border:"1px solid rgba(0,245,160,0.1)",fontSize:11,color:V.text3,lineHeight:1.6}}>
          💡 <strong style={{color:V.accent}}>Tomorrow:</strong> {program.message} Prioritize sleep and hydration today.
        </div>
      </div>
    );
  }

  return(
    <div style={{display:"flex",flexDirection:"column",gap:12}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <div>
          <div style={{fontSize:16,fontWeight:800,color:V.text}}>Today's Program</div>
          <div style={{fontSize:11,color:V.text3}}>{todayType} · Readiness {r.score}/100{s.profile?.sessionLength?` · ${s.profile.sessionLength} min`:""}{s.profile?.trainingTime?` · ${s.profile.trainingTime}`:""}</div>
        </div>
        <div style={{padding:"4px 10px",borderRadius:8,background:`${r.color}15`,border:`1px solid ${r.color}25`}}>
          <span style={{fontSize:11,fontWeight:700,color:r.color}}>{r.level}</span>
        </div>
      </div>

      {/* Injury-aware substitutions */}
      {program.skipped?.length>0&&(
        <div style={{padding:"8px 12px",borderRadius:8,background:`${V.danger}08`,border:`1px solid ${V.danger}20`}}>
          <span style={{fontSize:11,color:V.text2}}><span style={{fontWeight:700,color:V.danger}}>Working around your injuries:</span> skipped {program.skipped.join(", ")}{program.exercises.length?" and swapped in safer options where possible":""}.</span>
        </div>
      )}

      {/* Readiness-based note */}
      {r.score<60&&(
        <div style={{padding:"8px 12px",borderRadius:8,background:`${V.warn}08`,border:`1px solid ${V.warn}15`}}>
          <span style={{fontSize:11,color:V.warn}}>{program.message}</span>
        </div>
      )}

      {/* Start This Workout button — pre-loads suggested exercises into the logger */}
      {program.exercises.length>0&&(
        <Btn full onClick={()=>{
          const draft={date:today(),dur:"",exercises:program.exercises.map(ex=>({
            exerciseId:ex.id,
            sets:Array.from({length:ex.sets}).map(()=>({weight:ex.weight.toString(),reps:ex.reps.toString(),rpe:"",done:false}))
          })),notes:"Coach-suggested session",rating:3};
          LS.set("ft-coach-draft",draft);
          SuccessToastCtrl.show("Workout loaded — tap Log to start");
          d({type:"TAB",tab:"log_workout"});
        }} s={{background:"linear-gradient(135deg,#7c3aed,#4f46e5)",fontWeight:800}}>
          {Icons.dumbbell({size:15,color:"#fff"})} Start This Workout
        </Btn>
      )}
      {program.exercises.map((ex,i)=>(
        <Card key={ex.id} style={{padding:12}}>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <div style={{width:28,height:28,borderRadius:8,background:`${V.accent}12`,
              display:"flex",alignItems:"center",justifyContent:"center"}}>
              <span style={{fontSize:12,fontWeight:800,color:V.accent,fontFamily:V.mono}}>{i+1}</span>
            </div>
            <div style={{flex:1}}>
              <div style={{fontSize:13,fontWeight:700,color:V.text}}>{ex.name}</div>
              <div style={{fontSize:11,color:V.text3}}>
                {ex.sets}×{ex.reps} @ {ex.weight>0?`${ex.weight} ${u}`:"bodyweight"}
                <span style={{marginLeft:6,fontSize:11,color:ex.note.includes("↑")?V.accent:ex.note.includes("↓")?V.warn:V.text3,
                  fontWeight:600}}>{ex.note}</span>
              </div>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}

// ─── #3 Cloud Sync Status Bar ───
export function FormCheckTab({s,d}){
  const { isDesktop } = useLayout();
  const [selEx,setSelEx]=useState(null);
  const [recording,setRecording]=useState(false);
  const [videoUrl,setVideoUrl]=useState(null);
  const [checkedCues,setCheckedCues]=useState({});
  const toggleCue=(key)=>setCheckedCues(c=>({...c,[key]:!c[key]}));
  const mediaRef=useRef(null);
  const videoRef=useRef(null);
  const chunks=useRef([]);
  // Cleanup: stop camera stream when component unmounts
  useEffect(()=>{
    return()=>{
      if(mediaRef.current&&mediaRef.current.state==="recording")mediaRef.current.stop();
      if(videoRef.current?.srcObject)videoRef.current.srcObject.getTracks().forEach(t=>t.stop());
    };
  },[]);
  // #B7: Revoke blob URL whenever videoUrl changes (new recording replaces old) or on unmount.
  // Previously every recording created an unpinned blob URL that was never freed.
  useEffect(()=>{
    return()=>{if(videoUrl)URL.revokeObjectURL(videoUrl);};
  },[videoUrl]);

  const formCues={
    bench:["Bar path: straight up, slight arc back","Scapulae retracted and depressed","Feet flat, arch in lower back","Full ROM: bar to chest","Elbows ~45° angle"],
    squat:["Feet shoulder width, toes slightly out","Hit parallel or below — hip crease below knee","Knees track over toes","Chest up, neutral spine","Drive through full foot"],
    deadlift:["Bar over mid-foot","Hips hinge, not squat","Lats engaged, no rounding","Lockout: hips fully extended","Bar close to body throughout"],
    ohp:["Core braced, glutes tight","Bar starts at collarbone","Press straight up, head through","Full lockout overhead","No excessive back lean"],
    row:["Torso ~45° angle","Pull to lower chest/stomach","Squeeze shoulder blades","Control the eccentric","No excessive body English"],
    pullup:["Full hang at bottom","Pull chest to bar","Scapulae retract at top","Control descent","No kipping unless intended"],
    curl:["Elbows pinned to sides","Full ROM: extend and contract","No swinging body","Squeeze at top","Control negative"],
  };

  const defaultCues=["Full range of motion","Control the eccentric (lowering)","Maintain neutral spine","Breathe: exhale on exertion","Focus on mind-muscle connection"];

  const startRec=async()=>{
    try{
      const stream=await navigator.mediaDevices.getUserMedia({video:{facingMode:"environment"},audio:false});
      videoRef.current.srcObject=stream;
      videoRef.current.play();
      const mr=new MediaRecorder(stream);
      chunks.current=[];
      mr.ondataavailable=e=>{if(e.data.size>0)chunks.current.push(e.data);};
      mr.onstop=()=>{
        const blob=new Blob(chunks.current,{type:"video/webm"});
        setVideoUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach(t=>t.stop());
      };
      mediaRef.current=mr;
      mr.start();
      setRecording(true);
    }catch(e){
      SuccessToastCtrl.show("Camera access needed for form check");
    }
  };

  const stopRec=()=>{
    if(mediaRef.current&&mediaRef.current.state==="recording"){
      mediaRef.current.stop();
      setRecording(false);
    }
  };

  const exercises=s.exercises.filter(e=>["Chest","Back","Legs","Shoulders","Arms"].includes(e.cat));
  const cues=formCues[selEx]||defaultCues;

  return(
    <div style={{display:"flex",flexDirection:"column",gap:12}}>
      <div style={{fontSize:16,fontWeight:800,color:V.text}}>Form Check</div>
      <div style={{fontSize:12,color:V.text3}}>Record a set and review against form cues</div>

      {/* Exercise selector */}
      <div style={{display:"flex",gap:6,overflowX:"auto",paddingBottom:4,WebkitOverflowScrolling:"touch",scrollbarWidth:"none"}}>
        {exercises.map(ex=>(
          <button key={ex.id} onClick={()=>setSelEx(ex.id)} style={{
            padding:"6px 12px",borderRadius:8,border:`1.5px solid ${selEx===ex.id?V.accent:V.cardBorder}`,
            background:selEx===ex.id?`${V.accent}10`:V.card,
            cursor:"pointer",WebkitTapHighlightColor:"transparent",
            fontSize:11,fontWeight:600,color:selEx===ex.id?V.accent:V.text3,fontFamily:V.font
          }}>{ex.name.length>15?ex.name.slice(0,15)+"…":ex.name}</button>
        ))}
      </div>

      {selEx&&(
        <div>
          {/* Form cues checklist */}
          <Card style={{padding:12,marginBottom:10}}>
            <div style={{fontSize:12,fontWeight:700,color:V.text,marginBottom:8}}>Form Cues: {s.exercises.find(e=>e.id===selEx)?.name}</div>
            {cues.map((cue,i)=>{
              const key=`${selEx}-${i}`;
              const checked=!!checkedCues[key];
              return(
              <button key={i} onClick={()=>toggleCue(key)} style={{display:"flex",alignItems:"flex-start",gap:8,marginBottom:6,
                background:"none",border:"none",padding:"4px 0",cursor:"pointer",width:"100%",textAlign:"left",WebkitTapHighlightColor:"transparent"}}>
                <div style={{width:20,height:20,borderRadius:5,flexShrink:0,marginTop:1,transition:"all .15s",
                  background:checked?V.accent:"transparent",
                  border:`1.5px solid ${checked?V.accent:V.accent+"40"}`,
                  display:"flex",alignItems:"center",justifyContent:"center"}}>
                  {checked&&<span style={{fontSize:11,color:V.bg,fontWeight:900,lineHeight:1}}>✓</span>}
                </div>
                <span style={{fontSize:11,color:checked?V.text3:V.text2,lineHeight:1.5,textDecoration:checked?"line-through":"none",
                  transition:"color .15s"}}>{cue}</span>
              </button>
              );
            })}
          </Card>

          {/* Video area */}
          <Card style={{padding:12}}>
            <video ref={videoRef} style={{width:"100%",borderRadius:8,background:"#000",display:recording?"block":"none"}} playsInline muted/>
            {videoUrl&&!recording&&(
              <video src={videoUrl} style={{width:"100%",borderRadius:8}} controls playsInline/>
            )}
            <div style={{display:"flex",gap:8,marginTop:10}}>
              {!recording&&!videoUrl&&(
                <Btn full onClick={startRec}>{Icons.target({size:14,color:onAccent()})} Record Set</Btn>
              )}
              {recording&&(
                <Btn full onClick={stopRec} s={{background:V.danger}}>{Icons.x({size:14,color:"#fff"})} Stop</Btn>
              )}
              {videoUrl&&!recording&&(
                <div style={{display:"flex",gap:8,width:"100%"}}>
                  <Btn v="secondary" full onClick={()=>{setVideoUrl(null);}}>Retake</Btn>
                  <Btn full onClick={()=>{
                    const a=document.createElement("a");a.href=videoUrl;a.download=`formcheck-${selEx}-${today()}.webm`;a.click();
                  }}>{Icons.download({size:14,color:V.bg})} Save</Btn>
                </div>
              )}
            </div>
          </Card>
        </div>
      )}

      {!selEx&&(
        <Card style={{padding:20,textAlign:"center"}}>
          <div style={{fontSize:12,color:V.text3}}>Select an exercise above to see form cues and record a set</div>
        </Card>
      )}
    </div>
  );
}

// ─── #1 Data Integrity Safeguards ───
export function PhaseTracker({s,d}){
  const { isDesktop } = useLayout();
  const [showAdd,setShowAdd]=useState(false);
  const [form,setForm]=useState({type:"cut",start:today(),end:"",notes:""});
  const phases=normalizePhasesFromWire(s.phases||[]);

  const phaseTypes=[
    {id:"cut",label:"Cut",color:V.danger,icon:"🔥"},
    {id:"bulk",label:"Bulk",color:V.accent,icon:"💪"},
    {id:"maintain",label:"Maintain",color:V.accent2,icon:"⚖️"},
    {id:"strength",label:"Strength",color:V.purple,icon:"🏋️"},
    {id:"deload",label:"Deload",color:V.warn,icon:"🧘"},
    {id:"recomp",label:"Recomp",color:V.purple,icon:"♻️"},
  ];

  const addPhase=()=>{
    if(!form.start)return;
    const p={id:uid(),type:form.type,start:form.start,end:form.end||null,notes:form.notes,active:!form.end};
    d({type:"SET_PHASES",phases:[...phases,p]});
    SuccessToastCtrl.show("Phase started");
    setForm({type:"cut",start:today(),end:"",notes:""});setShowAdd(false);
  };

  const endPhase=(id)=>{d({type:"SET_PHASES",phases:phases.map(p=>p.id===id?{...p,end:today(),active:false}:p)});SuccessToastCtrl.show("Phase ended");};
  const deletePhase=(id)=>{d({type:"SET_PHASES",phases:phases.filter(p=>p.id!==id)});SuccessToastCtrl.show("Phase deleted");};

  const phaseStats=(phase)=>{
    const start=phaseStart(phase);
    const end=phaseEnd(phase);
    const ws=s.workouts.filter(w=>w.date>=start&&(!end||w.date<=end));
    const bs=s.body.filter(b=>b.date>=start&&(!end||b.date<=end)&&parseFloat(b.weight)>0).sort((a,b)=>b.date.localeCompare(a.date)); // newest first
    const ns=s.nutrition.filter(n=>n.date>=start&&(!end||n.date<=end));
    const startBW=bs.length?bs[bs.length-1].weight:null;
    const endBW=bs.length?bs[0].weight:null;
    const avgCal=ns.length?Math.round(ns.reduce((a,n)=>a+n.cal,0)/ns.length):0;
    const totalVol=ws.reduce((v,w)=>v+w.exercises.reduce((e,ex)=>e+ex.sets.reduce((ss,st)=>ss+(parseFloat(st.weight)||0)*(parseInt(st.reps)||0),0),0),0);
    const _noon=(x)=>new Date(x+"T12:00:00"); // local noon — bare YYYY-MM-DD parses as UTC midnight
    const days=end?Math.round((_noon(end)-_noon(start))/86400000):Math.round((_noon(today())-_noon(start))/86400000);
    return{workouts:ws.length,days,startBW,endBW,bwChange:startBW&&endBW?(endBW-startBW).toFixed(1):null,avgCal,totalVol};
  };

  const active=phases.find(p=>phaseIsActive(p));

  return(
    <div style={{display:"flex",flexDirection:"column",gap:12}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <div style={{fontSize:16,fontWeight:800,color:V.text}}>Training Phases</div>
        <button onClick={()=>setShowAdd(true)} style={{padding:"6px 12px",borderRadius:8,
          background:`${V.accent}12`,border:`1px solid ${V.accent}25`,cursor:"pointer",
          fontSize:11,fontWeight:700,color:V.accent,fontFamily:V.font}}>+ New Phase</button>
      </div>

      {active&&(()=>{
        const pt=phaseTypes.find(t=>t.id===phaseTypeId(active.type))||phaseTypes[2];
        const stats=phaseStats(active);
        return(
          <Card glow style={{padding:14}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <span style={{fontSize:18}}>{pt.icon}</span>
                <div>
                  <div style={{fontSize:14,fontWeight:700,color:V.text}}>Active: {pt.label}</div>
                  <div style={{fontSize:11,color:V.text3}}>Day {stats.days} · Started {phaseStart(active)}</div>
                </div>
              </div>
              <button onClick={()=>endPhase(active.id)} style={{padding:"5px 10px",borderRadius:6,
                background:`${V.warn}12`,border:`1px solid ${V.warn}25`,cursor:"pointer",fontSize:11,fontWeight:700,color:V.warn}}>End Phase</button>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8}}>
              {[{l:"Workouts",v:stats.workouts,c:V.accent},{l:"Avg Cal",v:stats.avgCal,c:V.warn},
                {l:"BW Change",v:stats.bwChange?`${stats.bwChange>0?"+":""}${stats.bwChange}`:"--",c:V.accent2}
              ].map(m=>(
                <div key={m.l} style={{textAlign:"center",padding:"8px 0",borderRadius:8,background:V.card}}>
                  <div style={{fontSize:16,fontWeight:800,color:m.c,fontFamily:V.mono}}>{m.v}</div>
                  <div style={{fontSize:11,color:V.text3,fontWeight:600}}>{m.l}</div>
                </div>
              ))}
            </div>
          </Card>
        );
      })()}

      {!active&&phases.length===0&&!showAdd&&(
        <Card style={{padding:20,textAlign:"center"}}>
          <div style={{fontSize:28,marginBottom:8}}>📊</div>
          <div style={{fontSize:13,fontWeight:600,color:V.text}}>No phases tracked yet</div>
          <div style={{fontSize:11,color:V.text3}}>Tag your training blocks to compare outcomes</div>
        </Card>
      )}

      {/* Past / scheduled phases — anything not currently active, including future starts */}
      {phases.filter(p=>!phaseIsActive(p)).length>0&&(
        <div>
          <div style={{fontSize:12,fontWeight:600,color:V.text3,marginBottom:6}}>Past Phases</div>
          {phases.filter(p=>!phaseIsActive(p)).sort((a,b)=>phaseStart(b).localeCompare(phaseStart(a))).map(p=>{
            const pt=phaseTypes.find(t=>t.id===phaseTypeId(p.type))||phaseTypes[2];
            const stats=phaseStats(p);
            return(
              <Card key={p.id} style={{padding:12,marginBottom:6}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                    <span>{pt.icon}</span>
                    <div>
                      <div style={{fontSize:12,fontWeight:700,color:V.text}}>{pt.label} · {stats.days}d</div>
                      <div style={{fontSize:11,color:V.text3}}>{phaseStart(p)} → {phaseEnd(p)||"open"}</div>
                    </div>
                  </div>
                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                    <div style={{textAlign:"right"}}>
                      <div style={{fontSize:11,fontWeight:700,color:V.accent,fontFamily:V.mono}}>{stats.workouts} workouts</div>
                      {stats.bwChange&&<div style={{fontSize:11,color:parseFloat(stats.bwChange)>0?V.accent:V.danger}}>{stats.bwChange>0?"+":""}{stats.bwChange} {s.units}</div>}
                    </div>
                    <button onClick={()=>deletePhase(p.id)} style={{background:"none",border:"none",cursor:"pointer",
                      fontSize:11,color:V.text3,padding:"2px 6px"}}>✕</button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {showAdd&&(
        <Sheet title="New Training Phase" onClose={()=>setShowAdd(false)}>
          <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:12}}>
            {phaseTypes.map(t=>(
              <button key={t.id} onClick={()=>setForm(f=>({...f,type:t.id}))} style={{
                padding:"8px 14px",borderRadius:8,border:`1.5px solid ${form.type===t.id?t.color:V.cardBorder}`,
                background:form.type===t.id?`${t.color}10`:V.card,
                cursor:"pointer",display:"flex",alignItems:"center",gap:6}}>
                <span>{t.icon}</span>
                <span style={{fontSize:12,fontWeight:600,color:form.type===t.id?t.color:V.text3}}>{t.label}</span>
              </button>
            ))}
          </div>
          <Field label="Start Date" type="date" value={form.start} onChange={v=>setForm(f=>({...f,start:v}))}/>
          <Field label="End Date (optional)" type="date" value={form.end} onChange={v=>setForm(f=>({...f,end:v}))}/>
          <Field label="Notes" value={form.notes} onChange={v=>setForm(f=>({...f,notes:v}))} placeholder="e.g. 500cal deficit, 4x/week"/>
          <Btn full onClick={addPhase} s={{marginTop:8}}>{Icons.check({size:16,color:onAccent()})} Start Phase</Btn>
        </Sheet>
      )}
    </div>
  );
}

// ─── #3 Exercise Substitution Intelligence ───
export function SubstitutionFinder({s}){
  const { isDesktop } = useLayout();
  const [selEx,setSelEx]=useState(null);
  const [q,setQ]=useState("");
  const subs=selEx?EXERCISE_SUBS[selEx]||[]:[];
  // Also suggest same-category exercises
  const selCat=s.exercises.find(e=>e.id===selEx)?.cat;
  const catSubs=selEx?s.exercises.filter(e=>e.cat===selCat&&e.id!==selEx&&!subs.includes(e.id)).slice(0,4):[];

  return(
    <div style={{display:"flex",flexDirection:"column",gap:12}}>
      <div style={{fontSize:16,fontWeight:800,color:V.text}}>Exercise Substitutions</div>
      <div style={{fontSize:12,color:V.text3}}>Find alternatives when equipment is unavailable</div>

      <Field label="Search exercise" value={q} onChange={setQ} placeholder="Type to filter, then tap an exercise"/>
      <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
        {s.exercises.filter(e=>!["Core","Cardio"].includes(e.cat)&&(!q||e.name.toLowerCase().includes(q.toLowerCase()))).slice(0,q?40:16).map(ex=>(
          <button key={ex.id} onClick={()=>setSelEx(ex.id)} style={{
            padding:"5px 10px",borderRadius:6,border:`1.5px solid ${selEx===ex.id?V.accent:V.cardBorder}`,
            background:selEx===ex.id?`${V.accent}10`:V.card,
            cursor:"pointer",fontSize:11,fontWeight:600,color:selEx===ex.id?V.accent:V.text3,fontFamily:V.font
          }}>{ex.name.length>18?ex.name.slice(0,18)+"…":ex.name}</button>
        ))}
      </div>

      {selEx&&(
        <div>
          <div style={{fontSize:12,fontWeight:700,color:V.text,marginBottom:6}}>Best alternatives for {s.exercises.find(e=>e.id===selEx)?.name}</div>
          {subs.length>0?(
            <div style={{display:"flex",flexDirection:"column",gap:6}}>
              {subs.map((sid,i)=>{
                const ex=s.exercises.find(e=>e.id===sid);
                if(!ex)return null;
                return(
                  <Card key={sid} style={{padding:12}}>
                    <div style={{display:"flex",alignItems:"center",gap:10}}>
                      <div style={{width:24,height:24,borderRadius:6,background:`${V.accent}12`,
                        display:"flex",alignItems:"center",justifyContent:"center"}}>
                        <span style={{fontSize:11,fontWeight:800,color:V.accent,fontFamily:V.mono}}>{i+1}</span>
                      </div>
                      <div>
                        <div style={{fontSize:13,fontWeight:600,color:V.text}}>{ex.name}</div>
                        <div style={{fontSize:11,color:V.text3}}>{ex.cat} · {i===0?"Best match":"Similar pattern"}</div>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          ):(
            <div style={{fontSize:11,color:V.text3}}>No preset substitutions. Try these from the same muscle group:</div>
          )}
          {catSubs.length>0&&(
            <div style={{marginTop:8}}>
              <div style={{fontSize:11,color:V.text3,fontWeight:600,marginBottom:4}}>Same muscle group</div>
              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                {catSubs.map(ex=>(
                  <span key={ex.id} style={{padding:"4px 10px",borderRadius:6,background:V.card,
                    border:`1px solid ${V.cardBorder}`,fontSize:11,color:V.text2}}>{ex.name}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── #4 Injury-Aware Training ───
export const EXERCISE_SUBS={
  // Chest
  bench:["dbbench","machpress","svbench","pushup"],
  incbench:["dbincbench","svbench","pushup"],
  dbbench:["bench","machpress","pushup"],
  flye:["dbflye","pecfly","pushup"],
  dip:["pushup","machpress","bench"],
  // Back
  deadlift:["rdl","sumo","hyperext","sldl"],
  row:["dbrow","cablerow","tbarrow","pendlay"],
  pullup:["latpull","chinup","straightarm"],
  latpull:["pullup","chinup","straightarm"],
  // Legs
  squat:["legpress","goblet","frontsquat","hacksquat"],
  legpress:["squat","hacksquat","goblet","bss"],
  lunge:["bss","stepup","goblet"],
  // Shoulders
  ohp:["dbohp","arnoldpress","machshoulder"],
  lateralraise:["cablateral","machlateral","reardelt"],
  // Arms
  curl:["hammercurl","preacher","spidercurl"],
  tricep:["skullcrusher","ohtriext","kickback","dip"],
};

export const JOINT_MAP={
  shoulder:["ohp","dbohp","lateralraise","arnoldpress","bench","incbench","dip","pushup","facepull","reardelt","cablateral","machlateral","machshoulder","svohp","landmine","zspress","handstandpush","behindneckpress","luyang","cleanjerk","snatch","pushjerk","thruster"],
  knee:["squat","frontsquat","hacksquat","legpress","lunge","bss","legext","stepup","goblet","walkinglunge","reverselunge","boxsquat","smithsquat","pendulumsguat","pistolsquat","beltquat","clean","thruster","singlelegpress"],
  lower_back:["deadlift","sumo","rdl","sldl","row","pendlay","hyperext","squat","goodmorning","rackpull","clean","snatch","hangclean","dbrdl","smithrdl","reversehy"],
  elbow:["curl","hammercurl","preacher","tricep","skullcrusher","ohtriext","kickback","bench","ohp","ezbar","inclinecurl","bayesian","spidercurl","closegrip","jmpress","21s"],
  wrist:["bench","ohp","curl","deadlift","row","pushup","clean","snatch","farmwalk","wristcurl","reversewrist"],
  hip:["squat","deadlift","hipthrust","lunge","bss","sumo","rdl","cablepullthrough","glutebridge","singlelegrdl","nordiccurl","goodmorning","hipabductor","hipadductor","pistolsquat"],
};

export function InjuryManager({s,d}){
  const { isDesktop } = useLayout();
  const [showAdd,setShowAdd]=useState(false);
  const [form,setForm]=useState({joint:"",severity:"moderate",notes:""});
  const injuries=normalizeInjuriesFromWire(s.injuries||[]);

  const joints=[
    {id:"shoulder",label:"Shoulder",icon:"🦾"},{id:"knee",label:"Knee",icon:"🦵"},
    {id:"lower_back",label:"Lower Back",icon:"🔙"},{id:"elbow",label:"Elbow",icon:"💪"},
    {id:"wrist",label:"Wrist",icon:"✋"},{id:"hip",label:"Hip",icon:"🦴"},
  ];

  const addInjury=()=>{
    if(!form.joint)return;
    d({type:"SET_INJURIES",injuries:[...injuries,{id:uid(),joint:form.joint,severity:form.severity,notes:form.notes,date:today(),active:true}]});
    SuccessToastCtrl.show("Injury flagged");
    setForm({joint:"",severity:"moderate",notes:""});setShowAdd(false);
  };

  const resolveInjury=(id)=>{d({type:"SET_INJURIES",injuries:injuries.map(inj=>inj.id===id?{...inj,active:false,resolved:today()}:inj)});SuccessToastCtrl.show("Injury resolved");};
  const deleteInjury=(id)=>{d({type:"SET_INJURIES",injuries:injuries.filter(inj=>inj.id!==id)});SuccessToastCtrl.show("Injury removed");};

  const activeInjuries=injuries.filter(injuryIsActive);
  const resolvedInjuries=injuries.filter(injuryIsResolved);
  const flaggedExercises=useMemo(()=>{
    const flagged=new Set();
    activeInjuries.forEach(inj=>{(JOINT_MAP[injuryJoint(inj)]||[]).forEach(eid=>flagged.add(eid));});
    return flagged;
  },[activeInjuries]);

  return(
    <div style={{display:"flex",flexDirection:"column",gap:12}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <div style={{fontSize:16,fontWeight:800,color:V.text}}>Injury Awareness</div>
        <button onClick={()=>setShowAdd(true)} style={{padding:"6px 12px",borderRadius:8,
          background:`${V.warn}12`,border:`1px solid ${V.warn}25`,cursor:"pointer",
          fontSize:11,fontWeight:700,color:V.warn,fontFamily:V.font}}>+ Flag Pain</button>
      </div>

      {activeInjuries.length>0?(
        <div>
          <div style={{padding:"8px 12px",borderRadius:8,background:`${V.danger}08`,border:`1px solid ${V.danger}15`,marginBottom:8}}>
            <span style={{fontSize:11,color:V.danger,fontWeight:600}}>{flaggedExercises.size} exercises flagged — consider substitutions or reduced load</span>
          </div>
          {activeInjuries.map(inj=>{
            const joint=injuryJoint(inj);
            const j=joints.find(j2=>j2.id===joint);
            const affected=(JOINT_MAP[joint]||[]).map(eid=>s.exercises.find(e=>e.id===eid)?.name).filter(Boolean);
            return(
              <Card key={inj.id} style={{padding:12,marginBottom:6}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:6}}>
                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                    <span style={{fontSize:16}}>{j?.icon||"⚠️"}</span>
                    <div>
                      <div style={{fontSize:13,fontWeight:700,color:V.text}}>{j?.label||joint}</div>
                      <div style={{fontSize:11,color:V.text3}}>Since {inj.date} · {inj.severity}</div>
                    </div>
                  </div>
                  <div style={{display:"flex",gap:6}}>
                    <button onClick={()=>resolveInjury(inj.id)} style={{padding:"4px 8px",borderRadius:6,
                      background:`${V.accent}12`,border:"none",cursor:"pointer",fontSize:11,color:V.accent,fontWeight:700}}>Resolved</button>
                    <button onClick={()=>deleteInjury(inj.id)} style={{padding:"4px 8px",borderRadius:6,
                      background:`${V.danger}14`,border:"none",cursor:"pointer",fontSize:11,color:V.danger}}>✕</button>
                  </div>
                </div>
                {inj.notes&&<div style={{fontSize:11,color:V.text3,fontStyle:"italic",marginBottom:6}}>{inj.notes}</div>}
                <div style={{fontSize:11,color:V.warn,fontWeight:600,marginBottom:4}}>Affected exercises:</div>
                <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
                  {affected.slice(0,8).map(n=>(
                    <span key={n} style={{padding:"2px 6px",borderRadius:4,background:`${V.warn}08`,fontSize:11,color:V.warn}}>{n}</span>
                  ))}

                </div>
              </Card>
            );
          })}
        </div>
      ):(
        <Card style={{padding:20,textAlign:"center"}}>
          <div style={{fontSize:28,marginBottom:8}}>💚</div>
          <div style={{fontSize:13,fontWeight:600,color:V.accent}}>No active injuries</div>
          <div style={{fontSize:11,color:V.text3}}>Flag pain points to get safer exercise suggestions</div>
        </Card>
      )}

      {resolvedInjuries.length>0&&(
        <div>
          <div style={{fontSize:12,fontWeight:600,color:V.text3,marginBottom:4}}>Resolved</div>
          {resolvedInjuries.map(inj=>(
            <div key={inj.id} style={{display:"flex",alignItems:"center",gap:8,padding:"6px 0",opacity:0.5}}>
              <span style={{fontSize:11}}>{joints.find(j=>j.id===injuryJoint(inj))?.icon||"✓"}</span>
              <span style={{fontSize:11,color:V.text3}}>{joints.find(j=>j.id===injuryJoint(inj))?.label||injuryJoint(inj)} · {inj.date} → {inj.resolved||"resolved"}</span>
              <span style={{flex:1}}/>
              <button onClick={()=>deleteInjury(inj.id)} style={{background:"none",border:"none",cursor:"pointer",fontSize:11,color:V.text3}}>✕</button>
            </div>
          ))}
        </div>
      )}

      {showAdd&&(
        <Sheet title="Flag Pain / Injury" onClose={()=>setShowAdd(false)}
          footer={<Btn full onClick={addInjury} disabled={!form.joint}>{Icons.check({size:16,color:onAccent()})} Flag Injury</Btn>}>
          <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:12}}>
            {joints.map(j=>(
              <button key={j.id} onClick={()=>setForm(f=>({...f,joint:j.id}))} style={{
                padding:"10px 14px",borderRadius:10,border:`1.5px solid ${form.joint===j.id?V.warn:V.cardBorder}`,
                background:form.joint===j.id?`${V.warn}10`:V.card,cursor:"pointer",
                display:"flex",alignItems:"center",gap:6}}>
                <span>{j.icon}</span>
                <span style={{fontSize:12,fontWeight:600,color:form.joint===j.id?V.warn:V.text3}}>{j.label}</span>
              </button>
            ))}
          </div>
          <div style={{display:"flex",gap:8,marginBottom:12}}>
            {["mild","moderate","severe"].map(sev=>(
              <button key={sev} onClick={()=>setForm(f=>({...f,severity:sev}))} style={{
                flex:1,padding:"8px",borderRadius:8,border:`1.5px solid ${form.severity===sev?(sev==="severe"?V.danger:sev==="moderate"?V.warn:V.accent2):V.cardBorder}`,
                background:form.severity===sev?`${sev==="severe"?V.danger:sev==="moderate"?V.warn:V.accent2}10`:V.card,
                cursor:"pointer",fontSize:11,fontWeight:600,color:form.severity===sev?V.text:V.text3,textTransform:"capitalize"}}>
                {sev}
              </button>
            ))}
          </div>
          <Field label="Notes (optional)" value={form.notes} onChange={v=>setForm(f=>({...f,notes:v}))} placeholder="e.g. Sharp pain on overhead press"/>
        </Sheet>
      )}
    </div>
  );
}

// ─── #6 Coach Summary Report ───
export function WorkoutCard({workout,s}){
  const canvasRef=useRef(null);
  const [generated,setGenerated]=useState(false);

  const generateCard=useCallback(()=>{
    const c=canvasRef.current;if(!c)return;
    const ctx=c.getContext("2d");
    c.width=600;c.height=400;
    const grad=ctx.createLinearGradient(0,0,600,400);
    grad.addColorStop(0,"#0a0a14");grad.addColorStop(1,"#14142a");
    ctx.fillStyle=grad;ctx.fillRect(0,0,600,400);
    ctx.fillStyle="#22d3ee";ctx.fillRect(0,0,4,400);
    ctx.fillStyle="#ffffff";ctx.font="bold 28px -apple-system, sans-serif";
    ctx.fillText("WORKOUT COMPLETE",24,48);
    ctx.fillStyle="#888";ctx.font="14px -apple-system, sans-serif";
    ctx.fillText(new Date(workout.date+"T12:00:00").toLocaleDateString("en-US",{weekday:"long",month:"long",day:"numeric"}),24,72);
    const totalSets=workout.exercises.reduce((n,e)=>n+e.sets.length,0);
    const totalVol=workout.exercises.reduce((v,e)=>v+e.sets.reduce((s2,st)=>s2+(parseFloat(st.weight)||0)*(parseInt(st.reps)||0),0),0);
    [{l:"Duration",v:`${workout.dur||0}min`},{l:"Exercises",v:workout.exercises.length},
      {l:"Sets",v:totalSets},{l:"Volume",v:totalVol>1000?`${(totalVol/1000).toFixed(1)}k`:totalVol}
    ].forEach((st,i)=>{
      const x=24+i*145;
      ctx.fillStyle="#22d3ee";ctx.font="bold 32px -apple-system, sans-serif";
      ctx.fillText(String(st.v),x,130);
      ctx.fillStyle="#666";ctx.font="11px -apple-system, sans-serif";
      ctx.fillText(st.l.toUpperCase(),x,148);
    });
    ctx.fillStyle="#444";ctx.fillRect(24,170,552,1);
    workout.exercises.slice(0,5).forEach((ex,i)=>{
      const y=198+i*36;
      const name=s.exercises.find(e=>e.id===ex.exerciseId)?.name||ex.exerciseId;
      // Coerce — string weights from form inputs make Math.max return NaN otherwise
      const weights=ex.sets.map(st=>parseFloat(st.weight)).filter(Number.isFinite);
      const best=weights.length?Math.max(...weights):0;
      ctx.fillStyle="#ddd";ctx.font="14px -apple-system, sans-serif";
      ctx.fillText(name,24,y);
      ctx.fillStyle="#22d3ee";ctx.font="bold 14px -apple-system, sans-serif";
      ctx.fillText(`${ex.sets.length}×${ex.sets[0]?.reps||0} @ ${best} ${wUnit(s.units)}`,400,y);
    });
    if(workout.exercises.length>5){ctx.fillStyle="#555";ctx.font="12px -apple-system, sans-serif";ctx.fillText(`+${workout.exercises.length-5} more exercises`,24,198+5*36);}
    ctx.fillStyle="#333";ctx.font="bold 12px -apple-system, sans-serif";ctx.fillText(appName(),24,380);
    ctx.fillStyle="#555";ctx.font="11px -apple-system, sans-serif";ctx.fillText("ironlog.space",110,380);
    if(workout.rating){ctx.fillStyle="#f59e0b";ctx.font="16px sans-serif";ctx.fillText("★".repeat(workout.rating),500,380);}
    setGenerated(true);
  },[workout]);

  // Auto-generate on mount — cancel on unmount or when generateCard changes
  useEffect(()=>{const t=setTimeout(generateCard,50);return()=>clearTimeout(t);},[generateCard]);

  const share=()=>{
    const c=canvasRef.current;if(!c)return;
    Haptic.light();
    c.toBlob(blob=>{
      if(navigator.share&&blob){
        navigator.share({files:[new File([blob],`workout-${workout.date}.png`,{type:"image/png"})],
          title:"Workout Complete",text:"Another one in the books 💪"}).catch(()=>{
          // Fallback to download
          const url=URL.createObjectURL(blob);const a=document.createElement("a");
          a.href=url;a.download=`workout-${workout.date}.png`;a.click();URL.revokeObjectURL(url);
          SuccessToastCtrl.show("Image saved");
        });
      }else{
        const url=URL.createObjectURL(blob);const a=document.createElement("a");
        a.href=url;a.download=`workout-${workout.date}.png`;a.click();URL.revokeObjectURL(url);
        SuccessToastCtrl.show("Image saved");
      }
    },"image/png");
  };

  const copyToClipboard=()=>{
    const c=canvasRef.current;if(!c)return;
    c.toBlob(async blob=>{
      try{await navigator.clipboard.write([new ClipboardItem({"image/png":blob})]);SuccessToastCtrl.show("Copied to clipboard");}
      catch(e){share();}
    },"image/png");
  };

  return(
    <div style={{display:"flex",flexDirection:"column",gap:8}}>
      <canvas ref={canvasRef} style={{width:"100%",borderRadius:12,display:generated?"block":"none"}}/>
      {generated&&(
        <div style={{display:"flex",gap:6}}>
          {flagOn("share_cards")&&<Btn full onClick={share}>{Icons.upload({size:14,color:onAccent()})} Share</Btn>}
          <Btn v="secondary" onClick={copyToClipboard} s={{flexShrink:0}}>{Icons.copy({size:14,color:V.text2})} Copy</Btn>
        </div>
      )}
    </div>
  );
}

// ─── #6 Form Check Workflow ───
